import { gql, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { PackageSearch, Stethoscope, type LucideIcon } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CatalogFloatingAlert, type CatalogFloatingAlertTone } from './CatalogFloatingAlert';
import { CREATE_PRODUCT_MUTATION, UPDATE_PRODUCT_MUTATION } from './products/products.gql';
import { CREATE_SERVICE_MUTATION } from './services/services.gql';

import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

type CatalogItemKind = 'product' | 'service';
type ProductPartialKindForm = 'none' | 'fractional_qty' | 'parent_with_doses';

const required_id_value = z
  .string()
  .trim()
  .min(1)
  .max(80);
const optional_id_value = z.string().trim().max(80);
const money_value = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/);
const optional_money_value = z
  .string()
  .trim()
  .refine((value) => value === '' || /^\d+(\.\d{1,2})?$/.test(value));
const quantity_value = z
  .string()
  .trim()
  .refine((value) => value === '' || /^\d+(\.\d{1,3})?$/.test(value));

const CATALOG_FORM_OPTIONS_QUERY = gql`
  query CatalogFormOptions {
    branches {
      id
      name
    }
    categories(limit: 200, status_filter: ACTIVE, sort_by: "created_at", sort_dir: DESC) {
      items {
        id
        name
        kind
      }
    }
    units(limit: 200, status_filter: ACTIVE) {
      items {
        id
        code
        name
      }
    }
    taxRates(limit: 200, status_filter: ACTIVE) {
      items {
        id
        code
        name
      }
    }
    products(limit: 200) {
      items {
        id
        code
        name
        partial_kind
      }
    }
  }
`;

const checkbox_value = z.boolean().catch(false);

const partial_kind_value = z.enum(['none', 'fractional_qty', 'parent_with_doses']).catch('none');
const parent_factor_value = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d+(\.\d{1,3})?$/.test(v), 'parent_factor_invalid')
  .optional();

const form_schema = z.object({
  item_kind: z.enum(['product', 'service']),
  code: z.string().trim().min(1).max(60),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(500).optional(),
  branch_id: required_id_value,
  category_id: optional_id_value,
  unit_id: optional_id_value,
  tax_rate_id: optional_id_value,
  price: money_value,
  cost: optional_money_value,
  stock_qty: quantity_value,
  stock_min: quantity_value,
  barcode: z.string().trim().max(80).optional(),
  is_controlled: checkbox_value,
  is_exception_handle: checkbox_value,
  is_quick_key: checkbox_value,
  requires_current_weight: checkbox_value,
  is_batch_tracked: checkbox_value,
  is_perishable: checkbox_value,
  partial_kind: partial_kind_value,
  parent_product_id: z.string().trim().optional(),
  parent_factor: parent_factor_value,
});

type FormValues = z.infer<typeof form_schema>;

export interface EditableProduct {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category_id: string | null;
  unit_id: string | null;
  tax_rate_id: string | null;
  price_cents: string;
  cost_cents: string;
  stock_qty: string;
  stock_min: string;
  is_controlled: boolean;
  is_exception_handle: boolean;
  requires_current_weight: boolean;
  is_batch_tracked: boolean;
  is_perishable: boolean;
  partial_kind: string;
  parent_product_id: string | null;
  parent_factor: string | null;
  barcode: string | null;
  branch_id: string | null;
}

interface CatalogItemFormDialogProps {
  initial_kind?: CatalogItemKind;
  product?: EditableProduct | null;
  onClose: () => void;
  onSaved: (kind: CatalogItemKind) => void | Promise<void>;
}

function toProductPartialKindGql(value: ProductPartialKindForm): 'NONE' | 'FRACTIONAL_QTY' | 'PARENT_WITH_DOSES' {
  switch (value) {
    case 'none':
      return 'NONE';
    case 'fractional_qty':
      return 'FRACTIONAL_QTY';
    case 'parent_with_doses':
      return 'PARENT_WITH_DOSES';
  }
}

interface FormOptionsResponse {
  branches: { id: string; name: string }[];
  categories: { items: { id: string; name: string; kind: string }[] };
  units: { items: { id: string; code: string; name: string }[] };
  taxRates: { items: { id: string; code: string; name: string }[] };
  products: { items: { id: string; code: string; name: string; partial_kind: string }[] };
}

export function CatalogItemFormDialog({
  initial_kind = 'product',
  product = null,
  onClose,
  onSaved,
}: CatalogItemFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const is_edit = product !== null;
  const { data, error: options_error } = useQuery<FormOptionsResponse>(CATALOG_FORM_OPTIONS_QUERY);
  const [createProduct, create_product_state] = useMutation(CREATE_PRODUCT_MUTATION);
  const [updateProduct, update_product_state] = useMutation(UPDATE_PRODUCT_MUTATION);
  const [createService, service_state] = useMutation(CREATE_SERVICE_MUTATION);
  const loading = create_product_state.loading || update_product_state.loading || service_state.loading;
  const [floating_alert, setFloatingAlert] = useState<{
    tone: CatalogFloatingAlertTone;
    title: string;
    body: string;
  } | null>(null);

  const default_branch_id = data?.branches[0]?.id ?? '';
  const form = useForm<FormValues>({
    resolver: zodResolver(form_schema),
    defaultValues: product
      ? {
          item_kind: 'product',
          code: product.code,
          name: product.name,
          description: product.description ?? '',
          branch_id: product.branch_id ?? default_branch_id,
          category_id: product.category_id ?? '',
          unit_id: product.unit_id ?? '',
          tax_rate_id: product.tax_rate_id ?? '',
          price: centsToMajorString(product.price_cents),
          cost: centsToMajorString(product.cost_cents),
          stock_qty: product.stock_qty,
          stock_min: product.stock_min,
          barcode: product.barcode ?? '',
          is_controlled: product.is_controlled,
          is_exception_handle: product.is_exception_handle,
          is_quick_key: false,
          requires_current_weight: product.requires_current_weight,
          is_batch_tracked: product.is_batch_tracked,
          is_perishable: product.is_perishable,
          partial_kind: product.partial_kind.toLowerCase() as FormValues['partial_kind'],
          parent_product_id: product.parent_product_id ?? '',
          parent_factor: product.parent_factor ?? '',
        }
      : {
          item_kind: initial_kind,
          code: '',
          name: '',
          description: '',
          branch_id: default_branch_id,
          category_id: '',
          unit_id: '',
          tax_rate_id: '',
          price: '',
          cost: '',
          stock_qty: '0',
          stock_min: '0',
          barcode: '',
          is_controlled: false,
          is_exception_handle: false,
          is_quick_key: false,
          requires_current_weight: false,
          is_batch_tracked: false,
          is_perishable: true,
          partial_kind: 'none',
          parent_product_id: '',
          parent_factor: '',
        },
  });
  const item_kind = form.watch('item_kind');
  const branch_id = form.watch('branch_id') ?? '';
  const category_id = form.watch('category_id') ?? '';
  const tax_rate_id = form.watch('tax_rate_id') ?? '';
  const unit_id = form.watch('unit_id') ?? '';
  const partial_kind = form.watch('partial_kind');
  const parent_product_id = form.watch('parent_product_id') ?? '';
  const errors = form.formState.errors;
  const eligible_parents = (data?.products.items ?? []).filter(
    (p) => p.partial_kind !== 'parent_with_doses',
  );

  useEffect(() => {
    if (!default_branch_id || form.getValues('branch_id')) return;
    form.setValue('branch_id', default_branch_id, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [default_branch_id, form]);

  useEffect(() => {
    if (!options_error) return;
    setFloatingAlert({
      tone: 'error',
      title: t('catalog.item_form.alert_options_title'),
      body: t('catalog.item_form.alert_options_body'),
    });
  }, [options_error, t]);

  const categories = useMemo(() => {
    return (data?.categories.items ?? []).filter((category) => {
      const kind = category.kind.toLowerCase();
      return kind === 'mixed' || kind === item_kind;
    });
  }, [data?.categories.items, item_kind]);

  useEffect(() => {
    if (!category_id) return;
    const category_exists = categories.some((category) => category.id === category_id);
    if (category_exists) return;
    form.setValue('category_id', '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [categories, category_id, form]);

  const onSubmit = async (values: FormValues): Promise<void> => {
    setFloatingAlert(null);
    const common = {
      code: values.code.trim(),
      name: values.name.trim(),
      description: values.description?.trim() ? values.description.trim() : null,
      category_id: values.category_id === '' ? null : values.category_id,
      tax_rate_id: values.tax_rate_id === '' ? null : values.tax_rate_id,
      branch_id: values.branch_id,
      price_cents: majorStringToCents(values.price),
    };

    if (values.item_kind === 'product') {
      const is_dose = values.partial_kind === 'parent_with_doses';
      const product_common = {
        unit_id: values.unit_id === '' ? null : values.unit_id,
        cost_cents: values.cost ? majorStringToCents(values.cost) : 0,
        stock_min: is_dose ? '0' : values.stock_min === '' ? '0' : values.stock_min,
        barcode: values.barcode?.trim() ? values.barcode.trim() : null,
        is_controlled: values.is_controlled,
        is_exception_handle: values.is_exception_handle,
        requires_current_weight: values.requires_current_weight,
        is_batch_tracked: values.is_batch_tracked,
        is_perishable: values.is_perishable,
        partial_kind: toProductPartialKindGql(values.partial_kind),
        parent_product_id: is_dose ? values.parent_product_id : null,
        parent_factor: is_dose ? values.parent_factor : null,
      };
      if (product) {
        const result = await updateProduct({
          variables: {
            id: product.id,
            input: {
              code: common.code,
              name: common.name,
              description: common.description,
              category_id: common.category_id,
              tax_rate_id: common.tax_rate_id,
              price_cents: common.price_cents,
              ...product_common,
            },
          },
        });
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0]?.message ?? 'update_product_failed');
        }
      } else {
        const result = await createProduct({
          variables: {
            input: {
              ...common,
              ...product_common,
              stock_qty: is_dose ? '0' : values.stock_qty === '' ? '0' : values.stock_qty,
            },
          },
        });
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0]?.message ?? 'create_product_failed');
        }
      }
    } else {
      const result = await createService({
        variables: {
          input: {
            ...common,
            is_quick_key: values.is_quick_key,
            requires_current_weight: values.requires_current_weight,
          },
        },
      });
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0]?.message ?? 'create_service_failed');
      }
    }

    await onSaved(values.item_kind);
  };

  const onInvalid = (errors: FieldErrors<FormValues>): void => {
    setFloatingAlert({
      tone: 'error',
      title: t('catalog.item_form.alert_validation_title'),
      body: validationAlertBody(errors, t),
    });
  };

  const onSubmitSafe = async (values: FormValues): Promise<void> => {
    try {
      await onSubmit(values);
    } catch (err) {
      setFloatingAlert({
        tone: 'error',
        title: t('catalog.item_form.alert_save_title'),
        body: saveErrorBody(err, t),
      });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="max-h-[92vh] w-full max-w-3xl overflow-auto">
        <form
          onSubmit={(event) => {
            void form.handleSubmit(onSubmitSafe, onInvalid)(event);
          }}
          noValidate
        >
          <input type="hidden" {...form.register('item_kind')} />
          <CardHeader>
            <CardTitle>{t('catalog.item_form.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {is_edit ? null : (
              <div className="grid gap-3 sm:grid-cols-2">
                <KindOption
                  active={item_kind === 'product'}
                  icon={PackageSearch}
                  title={t('catalog.item_form.product_kind')}
                  body={t('catalog.item_form.product_kind_body')}
                  onClick={() => {
                    form.setValue('item_kind', 'product', {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                />
                <KindOption
                  active={item_kind === 'service'}
                  icon={Stethoscope}
                  title={t('catalog.item_form.service_kind')}
                  body={t('catalog.item_form.service_kind_body')}
                  onClick={() => {
                    form.setValue('item_kind', 'service', {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                />
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <Field
                label={t('catalog.item_form.code')}
                error={errors.code ? t('catalog.item_form.alert_code_required') : undefined}
              >
                <Input {...form.register('code')} placeholder="VAC-001" />
              </Field>
              <Field
                label={t('catalog.item_form.name')}
                error={errors.name ? t('catalog.item_form.alert_name_required') : undefined}
              >
                <Input
                  {...form.register('name')}
                  placeholder={
                    item_kind === 'product' ? 'Vacuna múltiple canina' : 'Consulta general'
                  }
                />
              </Field>
              <Field
                label={t('catalog.item_form.branch')}
                error={errors.branch_id ? t('catalog.item_form.alert_branch_required') : undefined}
              >
                <Select
                  value={branch_id}
                  onChange={(event) => {
                    form.setValue('branch_id', event.target.value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                >
                  <option value="">{t('catalog.item_form.pick_branch')}</option>
                  {(data?.branches ?? []).map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('catalog.item_form.category')}
                error={errors.category_id ? t('catalog.item_form.alert_category_invalid') : undefined}
              >
                <Select
                  value={category_id}
                  onChange={(event) => {
                    form.setValue('category_id', event.target.value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                >
                  <option value="">{t('catalog.item_form.no_category')}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('catalog.item_form.price')}
                error={errors.price ? t('catalog.item_form.alert_price_required') : undefined}
              >
                <Input inputMode="decimal" {...form.register('price')} placeholder="450.00" />
              </Field>
              <Field
                label={t('catalog.item_form.tax_rate')}
                error={errors.tax_rate_id ? t('catalog.item_form.alert_tax_rate_invalid') : undefined}
              >
                <Select
                  value={tax_rate_id}
                  onChange={(event) => {
                    form.setValue('tax_rate_id', event.target.value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                >
                  <option value="">{t('catalog.item_form.no_tax_rate')}</option>
                  {(data?.taxRates.items ?? []).map((tax) => (
                    <option key={tax.id} value={tax.id}>
                      {tax.code} - {tax.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field
              label={t('catalog.item_form.description')}
              error={errors.description ? t('catalog.item_form.alert_description_invalid') : undefined}
            >
              <Input
                {...form.register('description')}
                placeholder={
                  item_kind === 'product'
                    ? 'Presentación, concentración o indicaciones internas'
                    : 'Notas operativas del servicio'
                }
              />
            </Field>

            {item_kind === 'product' ? (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t('catalog.item_form.partial_kind')}>
                    <Select
                      value={partial_kind}
                      onChange={(event) => {
                        form.setValue('partial_kind', event.target.value as FormValues['partial_kind'], {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                      }}
                    >
                      <option value="none">{t('catalog.item_form.partial_kind_none')}</option>
                      <option value="fractional_qty">
                        {t('catalog.item_form.partial_kind_fractional')}
                      </option>
                      <option value="parent_with_doses">
                        {t('catalog.item_form.partial_kind_dose')}
                      </option>
                    </Select>
                  </Field>
                  {partial_kind === 'parent_with_doses' ? (
                    <>
                      <Field label={t('catalog.item_form.parent_product')}>
                        <Select
                          value={parent_product_id}
                          onChange={(event) => {
                            form.setValue('parent_product_id', event.target.value, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            });
                          }}
                        >
                          <option value="">{t('catalog.item_form.parent_product_placeholder')}</option>
                          {eligible_parents.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code} — {p.name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={t('catalog.item_form.parent_factor')}>
                        <Input
                          inputMode="decimal"
                          step={0.001}
                          {...form.register('parent_factor')}
                          placeholder="0.050"
                        />
                      </Field>
                      <p className="md:col-span-2 text-xs text-muted-foreground">
                        {t('catalog.item_form.parent_factor_hint')}
                      </p>
                    </>
                  ) : null}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label={t('catalog.item_form.unit')}
                  error={errors.unit_id ? t('catalog.item_form.alert_unit_invalid') : undefined}
                >
                  <Select
                    value={unit_id}
                    onChange={(event) => {
                      form.setValue('unit_id', event.target.value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                  >
                    <option value="">{t('catalog.item_form.no_unit')}</option>
                    {(data?.units.items ?? []).map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.code} - {unit.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label={t('catalog.item_form.cost')}
                  error={errors.cost ? t('catalog.item_form.alert_cost_invalid') : undefined}
                >
                  <Input inputMode="decimal" {...form.register('cost')} placeholder="280.00" />
                </Field>
                {partial_kind !== 'parent_with_doses' ? (
                  <>
                    <Field
                      label={t('catalog.item_form.stock_qty')}
                      error={errors.stock_qty ? t('catalog.item_form.alert_stock_invalid') : undefined}
                    >
                      <Input inputMode="decimal" {...form.register('stock_qty')} placeholder="0" />
                    </Field>
                    <Field
                      label={t('catalog.item_form.stock_min')}
                      error={errors.stock_min ? t('catalog.item_form.alert_stock_min_invalid') : undefined}
                    >
                      <Input inputMode="decimal" {...form.register('stock_min')} placeholder="2" />
                    </Field>
                  </>
                ) : (
                  <p className="md:col-span-2 text-xs text-muted-foreground">
                    {t('catalog.item_form.dose_stock_hint')}
                  </p>
                )}
                <Field
                  label={t('catalog.item_form.barcode')}
                  error={errors.barcode ? t('catalog.item_form.alert_barcode_invalid') : undefined}
                >
                  <Input {...form.register('barcode')} placeholder="750..." />
                </Field>
                <div className="space-y-2 pt-7">
                  <Checkbox
                    label={t('catalog.item_form.controlled')}
                    {...form.register('is_controlled')}
                  />
                  <Checkbox
                    label={t('catalog.item_form.exception_handle')}
                    {...form.register('is_exception_handle')}
                  />
                  <Checkbox
                    label={t('catalog.item_form.requires_current_weight')}
                    {...form.register('requires_current_weight')}
                  />
                  <Checkbox
                    label={t('inventory.batch_tracked')}
                    {...form.register('is_batch_tracked')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('inventory.batch_tracked_hint')}
                  </p>
                  <Checkbox
                    label={t('inventory.perishable')}
                    {...form.register('is_perishable')}
                  />
                </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Checkbox
                  label={t('catalog.item_form.quick_key')}
                  {...form.register('is_quick_key')}
                />
                <Checkbox
                  label={t('catalog.item_form.requires_current_weight')}
                  {...form.register('requires_current_weight')}
                />
              </div>
            )}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('common.loading') : t('catalog.item_form.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {floating_alert ? (
        <CatalogFloatingAlert
          tone={floating_alert.tone}
          title={floating_alert.title}
          body={floating_alert.body}
          onClose={() => setFloatingAlert(null)}
        />
      ) : null}
    </div>
  );
}

interface KindOptionProps {
  active: boolean;
  icon: LucideIcon;
  title: string;
  body: string;
  onClick: () => void;
}

function KindOption({ active, icon, title, body, onClick }: KindOptionProps): JSX.Element {
  const Icon = icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border p-4 text-left transition ${
        active ? 'border-primary bg-primary/10' : 'border-border hover:bg-secondary/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon className={active ? 'text-primary mt-0.5 h-5 w-5' : 'mt-0.5 h-5 w-5'} aria-hidden />
        <div>
          <p className="font-medium">{title}</p>
          <p className="text-muted-foreground text-sm">{body}</p>
        </div>
      </div>
    </button>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  children: ReactNode;
}

function Field({ label, error, children }: FieldProps): JSX.Element {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </div>
  );
}

// forwardRef for the same reason as Checkbox: register()'s ref must reach the real <select> so the
// bound value (esp. defaultValues in edit mode) syncs to the DOM.
const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  (props, ref) => (
    <select
      {...props}
      ref={ref}
      className="border-input bg-background focus:ring-ring flex h-10 w-full rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
    />
  ),
);
Select.displayName = 'Select';

// Must forwardRef: react-hook-form's register() passes a ref, and React drops refs on plain
// function components — without this the checkbox's checked state never syncs and always saves false.
const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: string }
>(({ label, ...input }, ref) => (
  <label className="text-muted-foreground flex items-center gap-2 text-sm">
    <input type="checkbox" ref={ref} {...input} />
    <span>{label}</span>
  </label>
));
Checkbox.displayName = 'Checkbox';

function validationAlertBody(
  errors: FieldErrors<FormValues>,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (errors.branch_id) return t('catalog.item_form.alert_branch_required');
  if (errors.code) return t('catalog.item_form.alert_code_required');
  if (errors.name) return t('catalog.item_form.alert_name_required');
  if (errors.price) return t('catalog.item_form.alert_price_required');
  if (errors.category_id) return t('catalog.item_form.alert_category_invalid');
  if (errors.tax_rate_id) return t('catalog.item_form.alert_tax_rate_invalid');
  if (errors.unit_id) return t('catalog.item_form.alert_unit_invalid');
  if (errors.description) return t('catalog.item_form.alert_description_invalid');
  if (errors.cost) return t('catalog.item_form.alert_cost_invalid');
  if (errors.stock_qty) return t('catalog.item_form.alert_stock_invalid');
  if (errors.stock_min) return t('catalog.item_form.alert_stock_min_invalid');
  if (errors.barcode) return t('catalog.item_form.alert_barcode_invalid');
  return t('catalog.item_form.alert_validation_body');
}

function saveErrorBody(err: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  const message = err instanceof Error ? err.message : String(err);
  if (/duplicate|ER_DUP_ENTRY|already exists|unique/i.test(message)) {
    return t('catalog.item_form.alert_duplicate_code');
  }
  if (/forbidden|permission|unauthorized|missing_bearer/i.test(message)) {
    return t('catalog.item_form.alert_permission_error');
  }
  if (/branch|sucursal/i.test(message)) {
    return t('catalog.item_form.alert_branch_required');
  }
  return t('catalog.item_form.alert_save_body');
}
